// src/components/Modal.jsx
import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import PropTypes from 'prop-types';

const icons = {
  success: <CheckCircle size={48} className="text-green-600" />,
  error: <XCircle size={48} className="text-red-600" />,
  warning: <AlertCircle size={48} className="text-yellow-600" />,
  info: <Info size={48} className="text-blue-600" />,
};

const Modal = ({
  open = false,
  title = '',
  message = '',
  type = 'info',            // success | error | warning | info
  confirmText = 'OK',
  cancelText = 'Fechar',
  showCancel = false,
  onClose = () => {},
  onConfirm = null,
  closeOnOverlay = true,
  closeOnEsc = true,
}) => {
  useEffect(() => {
    if (!open) return;

    const handleKey = (e) => {
      if (e.key === 'Escape' && closeOnEsc) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, closeOnEsc, onClose]);

  if (!open) return null;

  const handleConfirm = () => {
    if (typeof onConfirm === 'function') {
      try {
        onConfirm();
      } catch (err) {
        console.error('Erro em onConfirm do modal:', err);
      }
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => closeOnOverlay && onClose()}
        aria-hidden="true"
      />

      {/* Modal box */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10 transform transition-all duration-200"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Fechar"
          title="Fechar"
        >
          <X size={20} />
        </button>

        {/* Icon */}
        <div className="flex flex-col items-center gap-3">
          <div>{icons[type]}</div>
          <h2 id="modal-title" className="text-lg font-bold text-gray-800 text-center">
            {title}
          </h2>
          {message && <p className="text-sm text-gray-600 text-center">{message}</p>}

          <div className="mt-5 w-full flex gap-3">
            {showCancel && (
              <button
                onClick={onClose}
                className="flex-1 py-2.5 border-2 border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                {cancelText}
              </button>
            )}

            <button
              onClick={handleConfirm}
              className="flex-1 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

Modal.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.string,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  showCancel: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  closeOnOverlay: PropTypes.bool,
  closeOnEsc: PropTypes.bool,
};

export default Modal;
